import React from 'react';
import { X, Download, Calendar, User, FileText, Activity } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { supabase } from '../../config/supabase';
import { AssessmentPDFService } from '../../services/AssessmentPDFService';

interface Assessment {
  id: string;
  patientId: string;
  patientName: string;
  doctorId?: string;
  doctorName?: string;
  type: string;
  tests?: string[];
  datetime: string;
  notes?: string;
  createdAt: any;
  data?: Record<string, any>;
}

interface AssessmentDetailModalProps {
  assessment: Assessment;
  onClose: () => void;
}

type MetricTone = 'primary' | 'neutral' | 'success' | 'warning' | 'danger';

interface SummaryCard {
  label: string;
  value?: React.ReactNode;
  helper?: string;
  tone?: MetricTone;
}

interface PrePostRow {
  label: string;
  rest?: React.ReactNode;
  end?: React.ReactNode;
  note?: string;
}

interface KeyValueItem {
  label: string;
  value?: React.ReactNode;
  hint?: string;
}

const toneClasses: Record<MetricTone, string> = {
  primary: 'text-primary-700',
  neutral: 'text-neutral-900',
  success: 'text-emerald-600',
  warning: 'text-amber-600',
  danger: 'text-rose-600'
};

const isNil = (value: any): boolean =>
  value === undefined ||
  value === null ||
  (typeof value === 'number' && Number.isNaN(value)) ||
  value === '';

const isFiniteNumber = (value: any): value is number => typeof value === 'number' && Number.isFinite(value);

const sanitizeTestType = (value: string | undefined | null): string =>
  typeof value === 'string' ? value.trim() : value ?? '';

const normalizeKey = (value: string): string =>
  sanitizeTestType(value).replace(/[\s/_-]/g, '').toLowerCase();

const getValueFromObject = (source: any, candidates: string[]): any => {
  if (!source || typeof source !== 'object') {
    return undefined;
  }

  for (const candidate of candidates) {
    if (candidate in source && !isNil(source[candidate])) {
      return source[candidate];
    }
  }

  const normalizedCandidates = new Set(
    candidates.map(candidate => normalizeKey(String(candidate)))
  );

  for (const key of Object.keys(source)) {
    if (normalizedCandidates.has(normalizeKey(key))) {
      const value = source[key];
      if (!isNil(value)) {
        return value;
      }
    }
  }

  return undefined;
};

const getSeriesValues = (series: any, candidates: string[]): number[] => {
  if (!Array.isArray(series)) {
    return [];
  }

  return series
    .map(entry => {
      const value = getValueFromObject(entry, candidates);
      return isFiniteNumber(value) ? value : undefined;
    })
    .filter((value): value is number => isFiniteNumber(value));
};

const getSeriesStat = (
  series: any,
  candidates: string[],
  mode: 'max' | 'min' | 'last'
): number | undefined => {
  const values = getSeriesValues(series, candidates);
  if (!values.length) {
    return undefined;
  }

  if (mode === 'last') {
    return values[values.length - 1];
  }

  return mode === 'max' ? Math.max(...values) : Math.min(...values);
};

const extractValue = (source: any, paths: string[]): any => {
  if (!source) {
    return undefined;
  }

  for (const path of paths) {
    const segments = path.split('.');
    let current = source;
    let matched = true;

    for (const segment of segments) {
      if (current == null) {
        matched = false;
        break;
      }

      if (Object.prototype.hasOwnProperty.call(current, segment)) {
        current = current[segment];
      } else if (segment in current) {
        current = current[segment];
      } else {
        matched = false;
        break;
      }
    }

    if (matched && !isNil(current)) {
      return current;
    }
  }

  return undefined;
};

const formatMetric = (value: any, unit = '', digits?: number): string | undefined => {
  if (isFiniteNumber(value)) {
    const formatted = typeof digits === 'number' ? value.toFixed(digits) : `${value}`;
    return unit ? `${formatted} ${unit}`.trim() : formatted;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    return value;
  }

  return undefined;
};

const formatPercent = (value: any, digits = 0): string | undefined => {
  if (isFiniteNumber(value)) {
    const fractionDigits = Number.isInteger(digits) ? digits : 0;
    return `${value.toFixed(fractionDigits)}%`;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    return value.includes('%') ? value : `${value}%`;
  }

  return undefined;
};

const formatBpm = (value: any): string | undefined => formatMetric(value, 'bpm');

const renderEmptyState = (message: string): React.ReactNode => (
  <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 px-4 py-6 text-center text-sm text-neutral-500">
    {message}
  </div>
);

const renderCards = (cards: SummaryCard[]): React.ReactNode => {
  const validCards = cards.filter(card => !isNil(card.value));
  if (!validCards.length) {
    return null;
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {validCards.map((card, index) => (
        <div
          key={`${card.label}-${index}`}
          className="rounded-xl border border-neutral-200 bg-white/70 p-4 shadow-sm"
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
            {card.label}
          </p>
          <div className={`mt-2 text-xl font-semibold ${toneClasses[card.tone ?? 'neutral']}`}>
            {card.value}
          </div>
          {card.helper && (
            <p className="mt-1 text-sm text-neutral-500">{card.helper}</p>
          )}
        </div>
      ))}
    </div>
  );
};

const renderPrePostTable = (rows: PrePostRow[]): React.ReactNode => {
  const validRows = rows.filter(row => !isNil(row.rest) || !isNil(row.end));
  if (!validRows.length) {
    return null;
  }

  return (
    <div className="overflow-hidden rounded-xl border border-neutral-200">
      <table className="w-full text-sm text-neutral-700">
        <thead className="bg-neutral-50 text-xs uppercase tracking-wide text-neutral-500">
          <tr>
            <th className="px-4 py-3 text-left font-semibold">Parâmetro</th>
            <th className="px-4 py-3 text-left font-semibold">Pré</th>
            <th className="px-4 py-3 text-left font-semibold">Pós</th>
          </tr>
        </thead>
        <tbody>
          {validRows.map((row, index) => (
            <tr
              key={row.label}
              className={index % 2 === 0 ? 'bg-white' : 'bg-neutral-50/60'}
            >
              <td className="px-4 py-3 font-medium text-neutral-800">
                <div className="flex flex-col">
                  <span>{row.label}</span>
                  {row.note && (
                    <span className="text-xs text-neutral-500">{row.note}</span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3">{!isNil(row.rest) ? row.rest : '-'}</td>
              <td className="px-4 py-3">{!isNil(row.end) ? row.end : '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const renderKeyValueList = (items: KeyValueItem[]): React.ReactNode => {
  const validItems = items.filter(item => !isNil(item.value) || !!item.hint);
  if (!validItems.length) {
    return null;
  }

  return (
    <div className="space-y-2">
      {validItems.map((item, index) => (
        <div
          key={`${item.label}-${index}`}
          className="flex items-start justify-between rounded-lg border border-neutral-200 bg-white/60 px-4 py-2"
        >
          <div className="pr-4">
            <p className="text-sm font-medium text-neutral-800">{item.label}</p>
            {item.hint && (
              <p className="text-xs text-neutral-500">{item.hint}</p>
            )}
          </div>
          <p className="text-sm font-semibold text-neutral-700">
            {!isNil(item.value) ? item.value : '-'}
          </p>
        </div>
      ))}
    </div>
  );
};

const renderGenericData = (data: any): React.ReactNode => {
  if (isNil(data) || (typeof data === 'object' && Object.keys(data).length === 0)) {
    return renderEmptyState('Dados não disponíveis para este teste.');
  }

  if (typeof data === 'object') {
    return (
      <pre className="whitespace-pre-wrap rounded-xl border border-neutral-200 bg-neutral-50 p-4 font-mono text-sm text-neutral-700">
        {JSON.stringify(data, null, 2)}
      </pre>
    );
  }

  return <p className="text-sm text-neutral-700">{String(data)}</p>;
};

const renderTC6Summary = (rawData: any): React.ReactNode => {
  if (!rawData) {
    return renderEmptyState('Resultados estruturados do TC6 não encontrados.');
  }

  const data = rawData.summary ?? rawData.result ?? rawData;

  const distance = extractValue(data, [
    'distance',
    'distance_m',
    'distanceMeters',
    'distance_walked',
    'results.distance'
  ]);
  const predictedDistance = extractValue(data, [
    'predictedDistance',
    'predicted.distance',
    'predictedDistanceMeters'
  ]);
  const percentPredicted = extractValue(data, [
    'percentPredicted',
    'predicted.percent',
    'distancePercent'
  ]);
  const steps = extractValue(data, ['steps', 'totalSteps']);
  const distanceDelta = extractValue(data, ['distanceDelta', 'deltaDistance', 'changeDistance']);

  const spo2Rest = extractValue(data, ['spo2.rest', 'spo2Rest', 'preSpo2', 'spo2.baseline']);
  const spo2End = extractValue(data, ['spo2.post', 'spo2End', 'postSpo2', 'spo2.min']);
  const hrRest = extractValue(data, ['heartRate.rest', 'hrRest', 'preHr']);
  const hrEnd = extractValue(data, ['heartRate.post', 'hrPost', 'peakHr']);
  const borgRest = extractValue(data, ['borg.rest', 'borgRest', 'rpe.rest']);
  const borgEnd = extractValue(data, ['borg.post', 'borgEnd', 'rpe.post']);
  const comments = extractValue(data, ['interpretation', 'notes', 'comment']);

  const tone: MetricTone =
    typeof percentPredicted === 'number'
      ? percentPredicted >= 80
        ? 'success'
        : percentPredicted >= 60
        ? 'warning'
        : 'danger'
      : 'neutral';

  const cards: SummaryCard[] = [
    {
      label: 'Distância percorrida',
      value: formatMetric(distance, 'm', 0),
      helper: predictedDistance ? `Predito: ${formatMetric(predictedDistance, 'm', 0)}` : undefined,
      tone: 'primary'
    },
    {
      label: '% do predito',
      value: formatPercent(percentPredicted),
      helper: !isNil(distanceDelta)
        ? `Delta distância: ${formatMetric(distanceDelta, 'm', 0) ?? distanceDelta}`
        : undefined,
      tone
    },
    {
      label: 'Passos',
      value: !isNil(steps) ? `${steps}` : undefined,
      helper: !isNil(borgEnd) ? `Borg final: ${borgEnd}` : undefined
    }
  ];

  const vitalsRows: PrePostRow[] = [
    { label: 'SpO2', rest: formatMetric(spo2Rest, '%', 0), end: formatMetric(spo2End, '%', 0) },
    { label: 'Frequência cardíaca', rest: formatBpm(hrRest), end: formatBpm(hrEnd) },
    { label: 'Escala de Borg', rest: !isNil(borgRest) ? borgRest : undefined, end: !isNil(borgEnd) ? borgEnd : undefined }
  ];

  return (
    <div className="space-y-4">
      {renderCards(cards)}
      {renderPrePostTable(vitalsRows)}
      {comments && (
        <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4">
          <p className="text-sm font-semibold text-neutral-800">Interpretação</p>
          <p className="mt-2 whitespace-pre-wrap text-sm text-neutral-700">{comments}</p>
        </div>
      )}
    </div>
  );
};

const renderCPETSummary = (rawData: any): React.ReactNode => {
  if (!rawData) {
    return renderEmptyState('Dados do teste cardiopulmonar não disponíveis.');
  }

  const summary = rawData.summary ?? rawData;
  const interpretation = rawData.interpretation ?? rawData.cpetInterpretation ?? rawData.analysis;

  const series: any[] =
    (Array.isArray(rawData?.series) && rawData.series) ||
    (Array.isArray(summary?.series) && summary.series) ||
    (Array.isArray(rawData?.rows) && rawData.rows) ||
    (Array.isArray(rawData?.data?.series) && rawData.data.series) ||
    [];

  let vo2L = extractValue(summary, ['vo2peak_lmin', 'vo2PeakLMin', 'vo2_peak_lmin']);
  if (isNil(vo2L)) {
    const fallback = getSeriesStat(series, ['vo2peak_lmin', 'vo2_l_min', 'vo2'], 'max');
    if (!isNil(fallback)) {
      vo2L = fallback;
    }
  }

  let vo2Mlkg = extractValue(summary, ['vo2peak_mlkg', 'vo2PeakMlKg', 'vo2_peak_mlkg']);
  if (isNil(vo2Mlkg)) {
    const fallback = getSeriesStat(series, ['vo2_ml_kg_min', 'vo2mlkg', 'vo2_mlkg'], 'max');
    if (!isNil(fallback)) {
      vo2Mlkg = fallback;
    }
  }

  const percentPredicted =
    extractValue(interpretation, ['functionalCapacity.percentPredicted']) ??
    extractValue(summary, ['vo2PercentPredicted', 'percentPredicted', 'vo2_percent_predicted']);
  const classification = extractValue(interpretation, ['functionalCapacity.classification']);

  let hrPeak = extractValue(summary, ['hr_peak', 'hrPeak']);
  if (isNil(hrPeak)) {
    const fallback = getSeriesStat(series, ['hr', 'fc', 'hr_peak', 'FC'], 'max');
    if (!isNil(fallback)) {
      hrPeak = fallback;
    }
  }

  let vevco2 = extractValue(summary, ['vevco2_slope', 'veVco2Slope']);
  if (isNil(vevco2)) {
    const fallback = getSeriesStat(series, ['ve_vco2', 'vevco2'], 'last');
    if (!isNil(fallback)) {
      vevco2 = fallback;
    }
  }

  let rerPeak = extractValue(summary, ['rer_peak', 'rerPeak']);
  if (isNil(rerPeak)) {
    const fallback = getSeriesStat(series, ['rer'], 'max');
    if (!isNil(fallback)) {
      rerPeak = fallback;
    }
  }

  let o2PulsePeak = extractValue(summary, ['o2pulse_peak_ml', 'o2PulsePeak']);
  if (isNil(o2PulsePeak)) {
    const fallback = getSeriesStat(
      series,
      ['o2_pulse_ml_per_beat', 'o2pulse', 'o2_pulse'],
      'max'
    );
    if (!isNil(fallback)) {
      o2PulsePeak = fallback;
    }
  }

  const vt1Percent = extractValue(summary, ['vt1_percent', 'vt1Percent', 'vt1_percent_vo2']);
  const vt2Percent = extractValue(summary, ['vt2_percent', 'vt2Percent', 'vt2_percent_vo2']);
  const hrVt1 = extractValue(summary, ['hr_vt1', 'vt1_hr', 'hrAtVt1']);
  const hrVt2 = extractValue(summary, ['hr_vt2', 'vt2_hr', 'hrAtVt2']);
  const oues = extractValue(summary, ['oues']);
  const primaryLimitation = extractValue(interpretation, ['primaryLimitation']);
  const clinicalSummary = extractValue(interpretation, ['clinicalSummary']);
  const recommendations = extractValue(interpretation, ['recommendations']);
  const trainingZones = extractValue(interpretation, ['trainingZones']);
  const additionalNotes =
    extractValue(rawData, ['notes', 'comment']) ?? extractValue(summary, ['notes']);

  const tone: MetricTone =
    typeof percentPredicted === 'number'
      ? percentPredicted >= 85
        ? 'success'
        : percentPredicted >= 70
        ? 'warning'
        : 'danger'
      : 'neutral';

  const cards: SummaryCard[] = [
    {
      label: 'VO2 pico',
      value: formatMetric(vo2L, 'L/min', 2),
      helper: vo2Mlkg ? formatMetric(vo2Mlkg, 'mL/kg/min', 1) : undefined,
      tone: 'primary'
    },
    {
      label: '% do VO2 predito',
      value: formatPercent(percentPredicted),
      helper: classification ? `Classificação: ${classification}` : undefined,
      tone
    },
    {
      label: 'FC pico',
      value: formatBpm(hrPeak),
      helper: o2PulsePeak ? `Pulso O2: ${formatMetric(o2PulsePeak, 'mL/bat', 1)}` : undefined
    },
    {
      label: 'VE/VCO2 slope',
      value: formatMetric(vevco2, undefined, 1),
      helper: oues ? `OUES: ${formatMetric(oues, undefined, 0)}` : undefined
    },
    {
      label: 'RER pico',
      value: formatMetric(rerPeak, undefined, 2)
    },
    {
      label: 'VT1',
      value: vt1Percent ? formatPercent(vt1Percent) : undefined,
      helper: hrVt1 ? `FC: ${formatBpm(hrVt1)}` : undefined
    }
  ];

  const vtRows: PrePostRow[] = [
    {
      label: 'VT1',
      rest: vt1Percent ? formatPercent(vt1Percent) : undefined,
      end: hrVt1 ? formatBpm(hrVt1) : undefined,
      note: 'Percentual VO2 / FC'
    },
    {
      label: 'VT2',
      rest: vt2Percent ? formatPercent(vt2Percent) : undefined,
      end: hrVt2 ? formatBpm(hrVt2) : undefined,
      note: 'Percentual VO2 / FC'
    }
  ];

  const cardsMarkup = renderCards(cards);
  const vtTableMarkup = renderPrePostTable(vtRows);
  const zonesMarkup =
    trainingZones && typeof trainingZones === 'object'
      ? (
          <div className="rounded-xl border border-neutral-200 bg-white/70 p-4">
            <p className="mb-3 text-sm font-semibold text-neutral-800">Zonas de treinamento</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {Object.values(trainingZones).map((zone: any, index) => (
                <div
                  key={zone?.name ?? index}
                  className="rounded-lg border border-neutral-200 bg-neutral-50/70 px-3 py-2"
                >
                  <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    {zone?.name ?? `Zona ${index + 1}`}
                  </p>
                  <p className="text-sm font-semibold text-neutral-800">
                    {zone?.hrRange ?? '-'}
                  </p>
                  {zone?.description && (
                    <p className="mt-1 text-xs text-neutral-500">{zone.description}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      : null;

  const summaryMarkup = clinicalSummary ? (
    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
      <p className="text-sm font-semibold text-amber-900">Resumo clínico</p>
      <p className="mt-1 whitespace-pre-wrap text-sm text-amber-900/90">
        {clinicalSummary}
      </p>
    </div>
  ) : null;

  const recommendationsMarkup =
    Array.isArray(recommendations) && recommendations.length > 0 ? (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
        <p className="text-sm font-semibold text-emerald-900">Recomendações</p>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-emerald-900/90">
          {recommendations.map((item: any, index: number) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      </div>
    ) : null;

  const limitationMarkup = primaryLimitation ? (
    <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3">
      <p className="text-sm font-semibold text-sky-800">Limitação principal</p>
      <p className="text-sm text-sky-800/90">{primaryLimitation}</p>
    </div>
  ) : null;

  const seriesInfoMarkup =
    !interpretation && !summary?.vo2peak_lmin && series.length > 0 ? (
      <div className="rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3">
        <p className="text-sm font-semibold text-neutral-800">
          Dados derivados da série do teste
        </p>
        <p className="mt-1 text-sm text-neutral-600">
          Os indicadores acima foram calculados a partir das amostras coletadas durante o teste
          ({series.length} registros). Gere o PDF para visualizar o laudo completo.
        </p>
      </div>
    ) : null;

  const additionalNotesMarkup = additionalNotes ? (
    <div className="rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-700 whitespace-pre-wrap">
      {additionalNotes}
    </div>
  ) : null;

  const hasContent =
    !!cardsMarkup ||
    !!vtTableMarkup ||
    !!limitationMarkup ||
    !!zonesMarkup ||
    !!summaryMarkup ||
    !!recommendationsMarkup ||
    !!seriesInfoMarkup ||
    !!additionalNotesMarkup;

  if (!hasContent) {
    return renderEmptyState('Dados estruturados do CPET não disponíveis.');
  }

  return (
    <div className="space-y-4">
      {cardsMarkup}
      {vtTableMarkup}
      {limitationMarkup}
      {zonesMarkup}
      {summaryMarkup}
      {recommendationsMarkup}
      {seriesInfoMarkup}
      {additionalNotesMarkup}
    </div>
  );
};

const renderCATSummary = (rawData: any): React.ReactNode => {
  if (!rawData) {
    return renderEmptyState('Resultados do CAT não encontrados.');
  }

  const total = extractValue(rawData, ['total', 'totalScore', 'score']);
  const impact = extractValue(rawData, ['impact', 'classification']);
  const lastScore = extractValue(rawData, ['trend.lastScore']);
  const responses = extractValue(rawData, ['responses', 'answers', 'items']);

  const severity =
    typeof total === 'number'
      ? total <= 9
        ? 'Impacto leve'
        : total <= 20
        ? 'Impacto moderado'
        : total <= 30
        ? 'Impacto alto'
        : 'Impacto muito alto'
      : impact;

  const cards: SummaryCard[] = [
    {
      label: 'Pontuação total',
      value: formatMetric(total, 'pts'),
      helper: severity ?? undefined,
      tone:
        typeof total === 'number'
          ? total <= 20
            ? 'success'
            : total <= 30
            ? 'warning'
            : 'danger'
          : 'neutral'
    },
    {
      label: 'Impacto percebido',
      value: severity ?? undefined,
      helper: lastScore ? `Última avaliação: ${formatMetric(lastScore, 'pts')}` : undefined
    }
  ];

  const responseEntries: KeyValueItem[] = [];

  if (Array.isArray(responses)) {
    responses.forEach((item: any, index: number) => {
      const label = item?.question ?? `Pergunta ${index + 1}`;
      const value = !isNil(item?.score) ? item.score : !isNil(item?.value) ? item.value : undefined;
      if (!isNil(value)) {
        responseEntries.push({ label, value: `${value}` });
      }
    });
  } else if (responses && typeof responses === 'object') {
    Object.entries(responses).forEach(([key, value]) => {
      if (!isNil(value)) {
        responseEntries.push({ label: key, value: `${value}` });
      }
    });
  }

  return (
    <div className="space-y-4">
      {renderCards(cards)}
      {responseEntries.length > 0 && (
        <div>
          <p className="mb-2 text-sm font-semibold text-neutral-800">Respostas</p>
          {renderKeyValueList(responseEntries)}
        </div>
      )}
    </div>
  );
};

const renderMMRCSummary = (rawData: any): React.ReactNode => {
  if (!rawData) {
    return renderEmptyState('Informações da escala mMRC não disponíveis.');
  }

  const grade = extractValue(rawData, ['grade', 'score', 'value', 'mmrc']);
  const description =
    extractValue(rawData, ['description', 'label']) ??
    (typeof grade === 'number'
      ? [
          'Sem limitação (0)',
          'Dispneia ao esforço intenso (1)',
          'Dispneia ao caminhar rápido em terreno plano (2)',
          'Dispneia ao caminhar 100 metros (3)',
          'Dispneia ao vestir-se ou sair de casa (4)'
        ][grade] ?? undefined
      : undefined);

  const cards: SummaryCard[] = [
    {
      label: 'Grau mMRC',
      value: !isNil(grade) ? `${grade}` : undefined,
      helper: description ?? undefined,
      tone:
        typeof grade === 'number'
          ? grade <= 1
            ? 'success'
            : grade <= 2
            ? 'warning'
            : 'danger'
          : 'neutral'
    }
  ];

  const cardsMarkup = renderCards(cards);

  return cardsMarkup ?? renderEmptyState('Escala não registrada.');
};

const renderPFSDQMSummary = (rawData: any): React.ReactNode => {
  if (!rawData) {
    return renderEmptyState('Dados do PFSDQ-M não disponíveis.');
  }

  const total = extractValue(rawData, ['totalScore', 'total', 'score']);
  const dyspnea = extractValue(rawData, ['dyspnea', 'domains.dyspnea.score']);
  const fatigue = extractValue(rawData, ['fatigue', 'domains.fatigue.score']);
  const activity = extractValue(rawData, ['activity', 'domains.activity.score']);
  const domains = extractValue(rawData, ['domains']);
  const notes = extractValue(rawData, ['interpretation', 'notes']);

  const cards: SummaryCard[] = [
    {
      label: 'Pontuação total',
      value: formatMetric(total, 'pts'),
      helper: '0 (melhor) - 10 (pior)'
    },
    {
      label: 'Dispneia',
      value: formatMetric(dyspnea, 'pts'),
      tone: 'warning'
    },
    {
      label: 'Fadiga',
      value: formatMetric(fatigue, 'pts')
    },
    {
      label: 'Atividade',
      value: formatMetric(activity, 'pts')
    }
  ];

  const domainEntries: KeyValueItem[] = [];
  if (domains && typeof domains === 'object' && !Array.isArray(domains)) {
    Object.entries(domains).forEach(([key, value]: [string, any]) => {
      const score = extractValue(value, ['score', 'value']);
      const hint = extractValue(value, ['description', 'label']);
      if (!isNil(score) || hint) {
        domainEntries.push({
          label: key,
          value: !isNil(score) ? `${score}` : undefined,
          hint: hint ?? undefined
        });
      }
    });
  }

  return (
    <div className="space-y-4">
      {renderCards(cards)}
      {domainEntries.length > 0 && (
        <div>
          <p className="mb-2 text-sm font-semibold text-neutral-800">Domínios</p>
          {renderKeyValueList(domainEntries)}
        </div>
      )}
      {notes && (
        <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-700 whitespace-pre-wrap">
          {notes}
        </div>
      )}
    </div>
  );
};

const renderSTSSummary = (testType: string, rawData: any): React.ReactNode => {
  if (!rawData) {
    return renderEmptyState('Dados não disponíveis para este teste.');
  }

  const normalized = normalizeKey(testType);
  const isTimeBased = normalized === '5xsts';

  const timeSeconds = extractValue(rawData, ['time', 'timeSeconds', 'seconds', 'duration']);
  const repetitions = extractValue(rawData, ['repetitions', 'reps', 'count']);
  const predicted = extractValue(rawData, ['predicted', 'predictedValue']);
  const percent = extractValue(rawData, ['percentPredicted', 'percent', 'percent_of_predicted']);
  const notes = extractValue(rawData, ['interpretation', 'notes']);

  const tone: MetricTone =
    typeof percent === 'number'
      ? percent >= 80
        ? 'success'
        : percent >= 60
        ? 'warning'
        : 'danger'
      : 'neutral';

  const cards: SummaryCard[] = [
    {
      label: isTimeBased ? 'Tempo total' : 'Repetições',
      value: isTimeBased ? formatMetric(timeSeconds, 's', 1) : !isNil(repetitions) ? `${repetitions}` : undefined,
      tone: 'primary'
    },
    {
      label: '% do predito',
      value: formatPercent(percent),
      helper: !isNil(predicted)
        ? `Predito: ${formatMetric(predicted, isTimeBased ? 's' : 'rep')}`
        : undefined,
      tone
    }
  ];

  const hrRest = extractValue(rawData, ['heartRate.rest', 'hrRest', 'preHr']);
  const hrPost = extractValue(rawData, ['heartRate.post', 'hrPost', 'postHr']);
  const spo2Rest = extractValue(rawData, ['spo2.rest', 'spo2Rest', 'preSpo2']);
  const spo2Post = extractValue(rawData, ['spo2.post', 'spo2Post', 'postSpo2']);
  const borgRest = extractValue(rawData, ['borg.rest', 'borgRest']);
  const borgPost = extractValue(rawData, ['borg.post', 'borgPost']);

  const vitalsRows: PrePostRow[] = [
    { label: 'SpO2', rest: formatMetric(spo2Rest, '%', 0), end: formatMetric(spo2Post, '%', 0) },
    { label: 'Frequência cardíaca', rest: formatBpm(hrRest), end: formatBpm(hrPost) },
    { label: 'Escala de Borg', rest: !isNil(borgRest) ? borgRest : undefined, end: !isNil(borgPost) ? borgPost : undefined }
  ];

  return (
    <div className="space-y-4">
      {renderCards(cards)}
      {renderPrePostTable(vitalsRows)}
      {notes && (
        <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-700 whitespace-pre-wrap">
          {notes}
        </div>
      )}
    </div>
  );
};

const renderHADSSummary = (rawData: any): React.ReactNode => {
  if (!rawData) {
    return renderEmptyState('Dados do HADS não disponíveis.');
  }

  const anxietyScore = extractValue(rawData, ['anxiety.score', 'anxiety', 'scores.anxiety']);
  const anxietyClass =
    extractValue(rawData, ['anxiety.classification', 'anxiety.label']) ??
    (typeof anxietyScore === 'number'
      ? anxietyScore <= 7
        ? 'Normal'
        : anxietyScore <= 10
        ? 'Limítrofe'
        : 'Anormal'
      : undefined);

  const depressionScore = extractValue(rawData, ['depression.score', 'depression', 'scores.depression']);
  const depressionClass =
    extractValue(rawData, ['depression.classification', 'depression.label']) ??
    (typeof depressionScore === 'number'
      ? depressionScore <= 7
        ? 'Normal'
        : depressionScore <= 10
        ? 'Limítrofe'
        : 'Anormal'
      : undefined);

  const total = extractValue(rawData, ['total', 'totalScore']);

  const cards: SummaryCard[] = [
    {
      label: 'Ansiedade (HADS-A)',
      value: formatMetric(anxietyScore, 'pts'),
      helper: anxietyClass ?? undefined,
      tone:
        typeof anxietyScore === 'number'
          ? anxietyScore <= 7
            ? 'success'
            : anxietyScore <= 10
            ? 'warning'
            : 'danger'
          : 'neutral'
    },
    {
      label: 'Depressão (HADS-D)',
      value: formatMetric(depressionScore, 'pts'),
      helper: depressionClass ?? undefined,
      tone:
        typeof depressionScore === 'number'
          ? depressionScore <= 7
            ? 'success'
            : depressionScore <= 10
            ? 'warning'
            : 'danger'
          : 'neutral'
    },
    {
      label: 'Pontuação total',
      value: formatMetric(total, 'pts')
    }
  ];

  return <div className="space-y-4">{renderCards(cards)}</div>;
};

const renderSGRQSummary = (rawData: any): React.ReactNode => {
  if (!rawData) {
    return renderEmptyState('Dados do SGRQ não disponíveis.');
  }

  const symptoms = extractValue(rawData, ['symptoms', 'domains.symptoms.score']);
  const activity = extractValue(rawData, ['activity', 'domains.activity.score']);
  const impact = extractValue(rawData, ['impact', 'domains.impact.score']);
  const total = extractValue(rawData, ['total', 'totalScore', 'overall']);
  const notes = extractValue(rawData, ['interpretation', 'notes']);

  const cards: SummaryCard[] = [
    { label: 'Total', value: formatPercent(total, 1), tone: 'primary' },
    { label: 'Sintomas', value: formatPercent(symptoms, 1) },
    { label: 'Atividade', value: formatPercent(activity, 1) },
    { label: 'Impacto', value: formatPercent(impact, 1) }
  ];

  return (
    <div className="space-y-4">
      {renderCards(cards)}
      {notes && (
        <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-700 whitespace-pre-wrap">
          {notes}
        </div>
      )}
    </div>
  );
};

const renderLCADLSummary = (rawData: any): React.ReactNode => {
  if (!rawData) {
    return renderEmptyState('Dados do LCADL não disponíveis.');
  }

  const total = extractValue(rawData, ['total', 'totalScore']);
  const domains = extractValue(rawData, ['domains']) ?? rawData;

  const domainEntries: KeyValueItem[] = [];
  if (domains && typeof domains === 'object') {
    Object.entries(domains).forEach(([key, value]: [string, any]) => {
      if (value && typeof value === 'object') {
        const score = extractValue(value, ['score', 'value']);
        const percent = extractValue(value, ['percent']);
        if (!isNil(score) || !isNil(percent)) {
          domainEntries.push({
            label: key,
            value: !isNil(percent) ? formatPercent(percent, 0) : !isNil(score) ? `${score}` : undefined,
            hint: !isNil(score) ? `Pontuação: ${score}` : undefined
          });
        }
      }
    });
  }

  const cards: SummaryCard[] = [
    {
      label: 'Pontuação total',
      value: formatMetric(total, 'pts'),
      helper: '0 (melhor) - 75 (pior)'
    }
  ];

  return (
    <div className="space-y-4">
      {renderCards(cards)}
      {domainEntries.length > 0 && (
        <div>
          <p className="mb-2 text-sm font-semibold text-neutral-800">Domínios</p>
          {renderKeyValueList(domainEntries)}
        </div>
      )}
    </div>
  );
};

const renderMLHFQSummary = (rawData: any): React.ReactNode => {
  if (!rawData) {
    return renderEmptyState('Dados do MLHFQ não disponíveis.');
  }

  const total = extractValue(rawData, ['total', 'totalScore']);
  const physical = extractValue(rawData, ['physical.score', 'physical', 'domains.physical.score']);
  const emotional = extractValue(rawData, ['emotional.score', 'emotional', 'domains.emotional.score']);
  const other = extractValue(rawData, ['other.score', 'other']);
  const notes = extractValue(rawData, ['interpretation', 'notes']);
  const responses = extractValue(rawData, ['responses', 'answers']);

  const classification =
    typeof total === 'number'
      ? total <= 24
        ? 'Impacto leve'
        : total <= 45
        ? 'Impacto moderado'
        : 'Impacto severo'
      : undefined;

  const cards: SummaryCard[] = [
    {
      label: 'Pontuação total',
      value: formatMetric(total, 'pts'),
      helper: classification ?? undefined,
      tone:
        typeof total === 'number'
          ? total <= 24
            ? 'success'
            : total <= 45
            ? 'warning'
            : 'danger'
          : 'neutral'
    },
    { label: 'Domínio físico', value: formatMetric(physical, 'pts') },
    { label: 'Domínio emocional', value: formatMetric(emotional, 'pts') },
    { label: 'Outros sintomas', value: formatMetric(other, 'pts') }
  ];

  const responseEntries: KeyValueItem[] = [];
  if (Array.isArray(responses)) {
    responses.forEach((item: any, index: number) => {
      const label = item?.question ?? `Pergunta ${index + 1}`;
      const value = !isNil(item?.score) ? item.score : !isNil(item?.value) ? item.value : undefined;
      if (!isNil(value)) {
        responseEntries.push({ label, value: `${value}` });
      }
    });
  }

  return (
    <div className="space-y-4">
      {renderCards(cards)}
      {responseEntries.length > 0 && (
        <div>
          <p className="mb-2 text-sm font-semibold text-neutral-800">Respostas</p>
          {renderKeyValueList(responseEntries)}
        </div>
      )}
      {notes && (
        <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-700 whitespace-pre-wrap">
          {notes}
        </div>
      )}
    </div>
  );
};

const renderPImaxSIndexSummary = (rawData: any): React.ReactNode => {
  if (!rawData) {
    return renderEmptyState('Dados do PImax e S-Index não disponíveis.');
  }

  const pimax = extractValue(rawData, ['pimax']) ?? rawData.pimax ?? rawData.PIMAX;
  const sIndex = extractValue(rawData, ['sindex', 'snip']) ?? rawData.sindex ?? rawData.SNIP;

  const pimaxMax = extractValue(pimax, ['max', 'value']);
  const pimaxPredicted = extractValue(pimax, ['predicted']);
  const pimaxPercent = extractValue(pimax, ['percentPredicted', 'percent']);
  const sIndexMax = extractValue(sIndex, ['max', 'value']);
  const notes = extractValue(rawData, ['interpretation', 'notes']);

  const pimaxMeasurements = extractValue(pimax, ['measurements']) ?? extractValue(pimax, ['history']);
  const sIndexMeasurements = extractValue(sIndex, ['measurements']) ?? extractValue(sIndex, ['history']);

  const tone: MetricTone =
    typeof pimaxPercent === 'number'
      ? pimaxPercent >= 80
        ? 'success'
        : pimaxPercent >= 60
        ? 'warning'
        : 'danger'
      : 'neutral';

  const cards: SummaryCard[] = [
    {
      label: 'PImax',
      value: formatMetric(pimaxMax, 'cmH2O', 1),
      helper: pimaxPercent ? `% do predito: ${formatPercent(pimaxPercent)}` : undefined,
      tone
    },
    {
      label: 'Valor predito (PImax)',
      value: formatMetric(pimaxPredicted, 'cmH2O', 1)
    },
    {
      label: 'S-Index (SNIP)',
      value: formatMetric(sIndexMax, 'cmH2O', 1),
      tone: 'primary'
    }
  ];

  const tableExists =
    (Array.isArray(pimaxMeasurements) && pimaxMeasurements.length > 0) ||
    (Array.isArray(sIndexMeasurements) && sIndexMeasurements.length > 0);

  return (
    <div className="space-y-4">
      {renderCards(cards)}
      {tableExists && (
        <div className="overflow-x-auto rounded-xl border border-neutral-200">
          <table className="min-w-full divide-y divide-neutral-200 text-sm text-neutral-700">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wide text-neutral-500">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Medida</th>
                <th className="px-4 py-3 text-left font-semibold">PImax (cmH2O)</th>
                <th className="px-4 py-3 text-left font-semibold">S-Index (cmH2O)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200/70">
              {Array.from({
                length: Math.max(
                  Array.isArray(pimaxMeasurements) ? pimaxMeasurements.length : 0,
                  Array.isArray(sIndexMeasurements) ? sIndexMeasurements.length : 0
                )
              }).map((_, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-neutral-50/60'}>
                  <td className="px-4 py-3 font-medium text-neutral-800">{`Medida ${index + 1}`}</td>
                  <td className="px-4 py-3">
                    {Array.isArray(pimaxMeasurements) && pimaxMeasurements[index] != null
                      ? formatMetric(pimaxMeasurements[index], 'cmH2O', 1)
                      : '-'}
                  </td>
                  <td className="px-4 py-3">
                    {Array.isArray(sIndexMeasurements) && sIndexMeasurements[index] != null
                      ? formatMetric(sIndexMeasurements[index], 'cmH2O', 1)
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {notes && (
        <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-700 whitespace-pre-wrap">
          {notes}
        </div>
      )}
    </div>
  );
};

const resolveTestData = (assessment: Assessment, testType: string): any => {
  const { data } = assessment;
  if (!data) {
    return undefined;
  }

  if (typeof data !== 'object' || Array.isArray(data)) {
    return data;
  }

  const sanitizedType = sanitizeTestType(testType);

  const candidateKeys = [
    sanitizedType,
    sanitizedType.toUpperCase(),
    sanitizedType.toLowerCase(),
    sanitizedType.replace(/-/g, '_'),
    sanitizedType.replace(/_/g, '-'),
    sanitizedType.replace(/[\s-]/g, ''),
    sanitizedType.replace(/[\s-]/g, '_')
  ];

  const record = data as Record<string, any>;

  for (const key of candidateKeys) {
    if (key in record && !isNil(record[key])) {
      return record[key];
    }
  }

  const normalized = normalizeKey(testType);
  for (const [key, value] of Object.entries(record)) {
    if (normalizeKey(key) === normalized) {
      return value;
    }
  }

  const nestedContainers = ['data', 'tests', 'results', 'summary'];
  for (const containerKey of nestedContainers) {
    const container = (record as Record<string, any>)[containerKey];
    if (container && typeof container === 'object') {
      const directNested = (container as Record<string, any>)[sanitizedType];
      if (!isNil(directNested)) {
        return directNested;
      }

      for (const [key, value] of Object.entries(container as Record<string, any>)) {
        if (normalizeKey(key) === normalizeKey(sanitizedType) && !isNil(value)) {
          return value;
        }
      }
    }
  }

  if (!assessment.tests || assessment.tests.length <= 1) {
    return data;
  }

  return data;
};

const renderTestContent = (testType: string, data: any): React.ReactNode => {
  const sanitized = sanitizeTestType(testType);
  const normalized = sanitized.replace(/[\s/_-]/g, '').toUpperCase();

  let payload = data;
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    const possiblePayload = getValueFromObject(payload, [
      sanitized,
      sanitized.toUpperCase(),
      sanitized.toLowerCase(),
      sanitized.replace(/-/g, '_'),
      sanitized.replace(/_/g, '-'),
      sanitized.replace(/[\s-]/g, ''),
      sanitized.replace(/[\s-]/g, '_')
    ]);
    if (!isNil(possiblePayload)) {
      payload = possiblePayload;
    }
  }

  if (normalized === 'TC6' || normalized === '6MWT') {
    return renderTC6Summary(payload);
  }

  if (normalized === 'CPET' || normalized === 'TCPE' || normalized === 'CPX') {
    return renderCPETSummary(payload);
  }

  if (normalized === 'CAT') {
    return renderCATSummary(payload);
  }

  if (normalized === 'MMRC') {
    return renderMMRCSummary(payload);
  }

  if (normalized === 'PFSDQM') {
    return renderPFSDQMSummary(payload);
  }

  if (normalized === '5XSTS') {
    return renderSTSSummary('5xSTS', payload);
  }

  if (normalized === 'STS30S' || normalized === 'STS30') {
    return renderSTSSummary('STS_30S', payload);
  }

  if (normalized === 'STS1MIN' || normalized === 'STS60S') {
    return renderSTSSummary('STS_1MIN', payload);
  }

  if (normalized === 'HADS') {
    return renderHADSSummary(payload);
  }

  if (normalized === 'SGRQ') {
    return renderSGRQSummary(payload);
  }

  if (normalized === 'LCADL') {
    return renderLCADLSummary(payload);
  }

  if (normalized === 'MLHFQ') {
    return renderMLHFQSummary(payload);
  }

  if (normalized === 'PIMAXSINDEX' || normalized === 'PIMAX') {
    return renderPImaxSIndexSummary(payload);
  }

  return renderGenericData(payload);
};

const typeLabels: Record<string, string> = {
  CAT: 'Questionário CAT',
  MMRC: 'Escala mMRC',
  'PFSDQ-M': 'PFSDQ-M (Status Funcional e Dispneia)',
  PFSDQM: 'PFSDQ-M (Status Funcional e Dispneia)',
  TC6: 'Teste de Caminhada de 6 minutos',
  '5XSTS': 'Sentar-Levantar 5x',
  'STS_30S': 'Sit-to-Stand (30s)',
  'STS_1MIN': 'Sit-to-Stand (1 min)',
  HADS: 'HADS (Ansiedade/Depressão)',
  PIMAX_SINDEX: 'PImax e S-Index',
  PIMAXSINDEX: 'PImax e S-Index',
  CPET: 'Teste Cardiopulmonar de Esforço',
  TCPE: 'Teste Cardiopulmonar de Esforço',
  CPX: 'Teste Cardiopulmonar de Esforço',
  SGRQ: 'Questionário Saint George (SGRQ)',
  LCADL: 'LCADL',
  MLHFQ: 'MLHFQ - Qualidade de Vida (Insuficiência Cardíaca)'
};

const getTestTypeLabel = (type: string): string => {
  const sanitized = sanitizeTestType(type);
  const normalized = sanitized.replace(/[\s/_-]/g, '').toUpperCase();
  return (
    typeLabels[sanitized] ??
    typeLabels[sanitized.toUpperCase()] ??
    typeLabels[normalized] ??
    sanitized
  );
};

const formatDateValue = (dateValue: any): string => {
  try {
    let date: Date;

    if (dateValue?.toDate && typeof dateValue.toDate === 'function') {
      date = dateValue.toDate();
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === 'string' || typeof dateValue === 'number') {
      date = new Date(dateValue);
    } else {
      return 'Data não disponível';
    }

    if (Number.isNaN(date.getTime())) {
      return 'Data não disponível';
    }

    return format(date, "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR });
  } catch (error) {
    return 'Data não disponível';
  }
};

const AssessmentDetailModal: React.FC<AssessmentDetailModalProps> = ({ assessment, onClose }) => {
  const [previousAssessments, setPreviousAssessments] = React.useState<Assessment[]>([]);

  React.useEffect(() => {
    const loadPreviousAssessments = async () => {
      try {
        const { data, error } = await supabase
          .from('medical_records')
          .select(`
            recordId,
            patientId,
            doctorId,
            recordTitle,
            recordDate,
            recordDescription,
            recordAttachments,
            recordCreatedAt,
            users(userDisplayName),
            patients(patientFullName)
          `)
          .eq('patientId', assessment.patientId)
          .eq('recordType', 'evaluation')
          .not('recordId', 'eq', assessment.id)
          .order('recordCreatedAt', { ascending: false })
          .limit(10);

        if (error) {
          throw error;
        }

        const mapped: Assessment[] = (data || []).map((item: any) => {
          const patient = Array.isArray(item.patients) ? item.patients[0] : item.patients;
          const doctor = Array.isArray(item.users) ? item.users[0] : item.users;

          return {
            id: item.recordId,
            patientId: item.patientId,
            patientName: patient?.patientFullName || assessment.patientName || 'Paciente',
            doctorId: item.doctorId,
            doctorName: doctor?.userDisplayName || undefined,
            type: item.recordTitle || 'Avaliação',
            tests: item.recordAttachments?.tests || undefined,
            datetime: item.recordDate,
            notes: item.recordDescription || '',
            createdAt: item.recordCreatedAt,
            data: item.recordAttachments?.data || item.recordAttachments || {},
          };
        });

        setPreviousAssessments(mapped);
      } catch (error) {
        console.error('Erro ao carregar avaliações anteriores para PDF:', error);
        setPreviousAssessments([]);
      }
    };

    loadPreviousAssessments();
  }, [assessment.id, assessment.patientId, assessment.patientName]);

  const testsToRender = React.useMemo(() => {
    const baseType = sanitizeTestType(assessment.type);
    const extras = Array.isArray(assessment.tests)
      ? assessment.tests
          .map(entry => sanitizeTestType(entry))
          .filter(entry => entry.length > 0)
      : [];
    const combined = extras.length ? [baseType, ...extras] : [baseType];
    return Array.from(new Set(combined.filter(Boolean)));
  }, [assessment]);

  const handleDownloadPDF = React.useCallback(async () => {
    try {
      await AssessmentPDFService.downloadPDF(assessment as any, previousAssessments as any);
      toast.success('PDF gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao baixar PDF:', error);
      toast.error('Erro ao gerar o PDF. Por favor, tente novamente.');
    }
  }, [assessment, previousAssessments]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full flex flex-col max-h-[90vh]">
        <div className="p-6 border-b border-neutral-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Activity className="w-8 h-8 text-primary-600" />
            <div>
              <h2 className="text-2xl font-display font-bold text-neutral-900">
                Detalhes da Avaliação
              </h2>
              <p className="text-sm text-neutral-600">
                {getTestTypeLabel(assessment.type)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-neutral-600" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-neutral-400 mt-1" />
                <div>
                  <p className="text-sm text-neutral-500">Paciente</p>
                  <p className="font-medium text-neutral-900">{assessment.patientName}</p>
                </div>
              </div>

              {assessment.doctorName && (
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-neutral-400 mt-1" />
                  <div>
                    <p className="text-sm text-neutral-500">Profissional</p>
                    <p className="font-medium text-neutral-900">{assessment.doctorName}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-neutral-400 mt-1" />
                <div>
                  <p className="text-sm text-neutral-500">Data da Avaliação</p>
                  <p className="font-medium text-neutral-900">
                    {formatDateValue(assessment.datetime || assessment.createdAt)}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-neutral-400 mt-1" />
                <div>
                  <p className="text-sm text-neutral-500">Tipo</p>
                  <p className="font-medium text-neutral-900">
                    {getTestTypeLabel(assessment.type)}
                  </p>
                </div>
              </div>
            </div>

            {testsToRender.length > 1 && (
              <div>
                <h3 className="font-semibold text-neutral-900 mb-3">Testes incluídos</h3>
                <div className="flex flex-wrap gap-2">
            {testsToRender.map((test, index) => (
              <span
                key={`${test}-${index}`}
                className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm"
              >
                {index === 0 ? `${getTestTypeLabel(test)} (principal)` : getTestTypeLabel(test)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {testsToRender.length > 0 ? (
              <div className="space-y-4">
                {testsToRender.map((testType, index) => {
                  const sanitizedType = sanitizeTestType(testType);
                  const resolvedData = resolveTestData(assessment, sanitizedType);
                  const sectionContent = renderTestContent(sanitizedType, resolvedData);

                  return (
                    <section
                      key={`${testType}-${index}`}
                      className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-100 bg-neutral-50 px-4 py-3 sm:px-6">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
                            {testsToRender.length > 1 ? `Teste ${index + 1}` : 'Teste'}
                          </p>
                          <h3 className="text-lg font-semibold text-neutral-900">
                            {getTestTypeLabel(testType)}
                          </h3>
                        </div>
                        <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary-700">
                          {index === 0 ? 'Principal' : 'Complementar'}
                        </span>
                      </div>
                      <div className="space-y-4 px-4 py-4 sm:px-6">
                        {sectionContent || renderEmptyState('Dados não disponíveis para este teste.')}
                      </div>
                    </section>
                  );
                })}
              </div>
            ) : (
              renderEmptyState('Nenhum teste associado a esta avaliação.')
            )}

            {assessment.notes && (
              <div>
                <h3 className="font-semibold text-neutral-900 mb-3">Observações</h3>
                <div className="bg-neutral-50 rounded-lg p-4 border border-neutral-200">
                  <p className="text-neutral-700 whitespace-pre-wrap">{assessment.notes}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-neutral-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-neutral-300 rounded-lg text-neutral-700 hover:bg-neutral-50 transition-colors"
          >
            Fechar
          </button>
          <button
            onClick={handleDownloadPDF}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Baixar PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssessmentDetailModal;
